import { ChooseFileButton } from '@components/Button/ChooseFileButton';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from '@partful/shared/ui/Input';
import { Typography } from '@partful/shared/ui/Typography';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './FileInput.module.scss';

type FileInputProps<T extends FieldValues & { file: File | null }> = {
  control: Control<T>;
  accept: string;
  placeholder?: string;
};

export function FileInput<T extends FieldValues & { file: File | null }>({
  control,
  accept,
  placeholder,
}: FileInputProps<T>) {
  const { t } = useTranslation('common');

  return (
    <Controller
      control={control}
      name={'file' as Path<T>}
      render={({ field: { onChange, onBlur, value, ref, disabled }, fieldState: { error } }) => (
        <div className={styles.root}>
          <div className={styles.fileInput}>
            <Input
              error={error !== undefined}
              type="text"
              placeholder={placeholder || t('fileUpload.placeholder')}
              data-testid="file-input"
              required
              onBlur={onBlur}
              disabled={disabled}
              value={value?.name ?? ''}
              ref={ref}
              className={styles.fullWidth}
            />
            <ChooseFileButton component="label" variant="contained" size="large" data-testid="upload-browse-button">
              {t('fileUpload.browse')}
              <input
                type="file"
                accept={accept}
                data-testid="hidden-file-input"
                onChange={(event) => onChange(event.target.files?.[0])}
                className={styles.hiddenInput}
                aria-errormessage={error ? 'fileInputError' : undefined}
                aria-invalid={error ? true : undefined}
              />
            </ChooseFileButton>
          </div>
          {error && (
            <Typography id="fileInputError" appearance="label" className={styles.errorMessage} role="alert">
              <FontAwesomeIcon icon={faTriangleExclamation} /> {error.message}
            </Typography>
          )}
        </div>
      )}
    />
  );
}
